import { FC } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { ChartEmptyState } from '../../leopart-update/components/AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import {
  chartLegendStyle,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from './chartTheme';
import { DistributionSlice } from './chart.types';
import styles from './DistributionDonut.module.scss';

interface IDistributionDonutProps {
  data: DistributionSlice[];
  height?: number;
  /** Formatta il valore grezzo (es. minuti → "1h 20m"). Default: numero puro. */
  valueFormatter?: (value: number) => string;
}

/**
 * Donut generico con legenda tabellare a fianco — colore e formattazione
 * arrivano dal chiamante, mai da un array locale (vedi `chart.types.ts`).
 *
 * Promosso da `PhaseDistributionChart` (TrainingStatisticsDashboard): stessa
 * composizione (pie + tabella), generalizzata su `DistributionSlice`.
 */
export const DistributionDonut: FC<IDistributionDonutProps> = ({
  data,
  height = 280,
  valueFormatter,
}) => {
  if (!data || data.length === 0) return <ChartEmptyState />;
  const format = valueFormatter ?? ((value: number) => String(value));

  return (
    <div className={styles.container}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={3}
            dataKey="value"
            nameKey="label"
          >
            {data.map((slice) => (
              <Cell key={slice.id} fill={slice.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={chartTooltipContentStyle}
            labelStyle={chartTooltipLabelStyle}
            itemStyle={chartTooltipItemStyle}
            formatter={(value, name) => {
              const slice = data.find((s) => s.label === name);
              const pct = slice?.percent != null ? ` (${slice.percent}%)` : '';
              return [`${format(Number(value))}${pct}`, name];
            }}
          />
          <Legend iconType="circle" iconSize={10} wrapperStyle={chartLegendStyle} />
        </PieChart>
      </ResponsiveContainer>

      <div className={styles.table}>
        {data.map((slice) => (
          <div key={slice.id} className={styles.row}>
            <span className={styles.dot} style={{ background: slice.color }} />
            <span className={styles.label}>{slice.label}</span>
            <span className={styles.value}>{format(slice.value)}</span>
            <span className={styles.percent}>{slice.percent != null ? `${slice.percent}%` : '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DistributionDonut;
